import React, { useState } from 'react';
import styled from 'styled-components';
import { apiProvider } from "@process-advisor/process-miner/api/apiProvider";
import { getSourceSystem } from '@process-advisor/process-miner/processMiner/processMiner';
import { getCapabilitiesForSource } from '@process-advisor/process-miner/sources/capabilities';
import { getMetricsProfileForSource } from '@process-advisor/process-miner/sources/metricsProfile';
import { Event } from '@process-advisor/process-miner/api/events';
import TransitionInsights from '@process-advisor/process-miner/dashboard/transitions/transitionInsights';
import VariantInsights from '@process-advisor/process-miner/dashboard/variants/variantsInsights';
import MetricsDashboard from '@process-advisor/process-miner/dashboard/metrics/metricsDashboard';
import SegmentInsights from '@process-advisor/process-miner/dashboard/segments/segmentInsights';
import SlaMetrics from '@process-advisor/process-miner/dashboard/slaMetrics/slaMetrics';
import DiscoveryRules from '@process-advisor/process-miner/dashboard/discoveryRules/discoveryRules';

import { 
  BarChart3, 
  SlidersHorizontal,
  Zap,
  GitCompareArrows,
  Workflow,
  ShieldAlert,
  TrendingUp,
  Clock,
  Sparkles,
} from 'lucide-react';

const Dashboard = ({AI, aiInsightsState, setAiInsightsState, license, licenseType, processLabel='Process', entityLabel='Work Item', initialTab = null, hideNav = false, reducedPadding = false, model = {}}) => {
  const [activeTab, setActiveTab] = useState(initialTab || 'metrics');
  // Sync when AppNav drives the sub-tab from outside
  React.useEffect(() => {
    if (initialTab && initialTab !== activeTab) setActiveTab(initialTab);
  }, [initialTab]);
  const [complianceDiscoveryRules, setComplianceDiscoveryRules] = useState([]);
  const [performanceDiscoveryRules, setPerformanceDiscoveryRules] = useState([]);
  const [automationOpportunities, setAutomationOpportunities] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  React.useEffect(() => {
    const listener = () => {
      updateContent();
      setRefreshKey(prev => prev + 1);
    };
    listener();

    apiProvider.on(Event.setActionBar_Dashboard, listener);
    return () => {
      apiProvider.off(Event.setActionBar_Dashboard, listener);
    };
  }, []);

  const updateContent = () => {
    
    const rows = apiProvider.processDiscoveryRulesSummary();
    const automationTemp = [];
    const complianceTemp = [];
    const performanceTemp = [];

    if (rows) {
      rows.forEach(row => {
        if (row.tasks > 0) {
          if (row.category === 'Compliance') complianceTemp.push(row);
          if (row.category === 'Performance') performanceTemp.push(row);
          if (row.automation_opportunity === true) automationTemp.push(row);
        }
      });
    }
    
    setAutomationOpportunities(automationTemp);
    setPerformanceDiscoveryRules(performanceTemp);
    setComplianceDiscoveryRules(complianceTemp);
  };

  const sourceSystem = getSourceSystem ? getSourceSystem() : 'generic';
  const capabilities = getCapabilitiesForSource(sourceSystem);
  const metricsProfile = getMetricsProfileForSource(sourceSystem);

  const allTabs = [
    { id: 'metrics', label: 'Process Metrics', icon: BarChart3, color: '#0052CC' },
    { id: 'slaMetrics', label: 'SLA Metrics', icon: Clock, color: '#0052CC', capability: 'slaMetrics' },
    { id: 'segments', label: 'Segments', icon: SlidersHorizontal, color: '#0052CC', capability: 'evidenceSegments' },
    { id: 'variants', label: 'Variants', icon: Workflow, color: '#0052CC' },
    { id: 'transitions', label: 'Transitions', icon: GitCompareArrows, color: '#0052CC' },
    { id: 'performance', label: 'Performance Rules', icon: TrendingUp, color: '#0052CC' },
    { id: 'automation', label: 'Automation Opportunities', icon: Zap, color: '#0052CC' },
    { id: 'compliance', label: 'Compliance Rules', icon: ShieldAlert, color: '#0052CC' },
  ];

  // Filter tabs - only show automation tab if there are automation opportunities
  const tabs = allTabs.filter(tab => {
    if (metricsProfile?.dashboardTabs && !metricsProfile.dashboardTabs.includes(tab.id) && ['activityMetrics', 'slaMetrics', 'segments'].includes(tab.id)) {
      return false;
    }
    if (tab.capability && !capabilities[tab.capability]) {
      return false;
    }
    if (tab.id === 'automation') {
      return automationOpportunities.length > 0;
    }
    return true;
  });

  const renderContent = () => {
    const contentMap = {
      metrics: <MetricsDashboard AI={AI} refreshKey={refreshKey} processLabel={processLabel} entityLabel={entityLabel}/>,
      // Legacy/deep-link fallback: Activity Analysis has been consolidated into Process Metrics.
      activityMetrics: <MetricsDashboard AI={AI} refreshKey={refreshKey} processLabel={processLabel} entityLabel={entityLabel}/>,
      slaMetrics: capabilities.slaMetrics ? <SlaMetrics refreshKey={refreshKey} processLabel={processLabel} entityLabel={entityLabel}/> : <MetricsDashboard AI={AI} refreshKey={refreshKey} processLabel={processLabel} entityLabel={entityLabel}/>,
      segments: capabilities.evidenceSegments ? <SegmentInsights refreshKey={refreshKey} processLabel={processLabel} entityLabel={entityLabel}/> : <MetricsDashboard AI={AI} refreshKey={refreshKey} processLabel={processLabel} entityLabel={entityLabel}/>,
      variants: <VariantInsights AI={AI} refreshKey={refreshKey} processLabel={processLabel}/>,
      transitions: <TransitionInsights AI={AI} refreshKey={refreshKey} processLabel={processLabel}/>,
      performance: <DiscoveryRules type='performance' discoveryRules={performanceDiscoveryRules} AI={AI} model={model}/>,
      automation: <DiscoveryRules type='automation' discoveryRules={automationOpportunities} AI={AI}/>,
      compliance: <DiscoveryRules type='compliance' discoveryRules={complianceDiscoveryRules} AI={AI} model={model}/>,
    };
    
    return <div className="content-container">{contentMap[activeTab] || contentMap.metrics}</div>;
  };

  return (
    <div className="atlassian-dashboard">
      <style jsx>{`
        .atlassian-dashboard {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
          color: #172B4D;
          background: #F4F5F7;
          border: 1px solid #DFE1E6;
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(9,30,66,0.10), 0 0 1px rgba(9,30,66,0.08);
          overflow: hidden;
        }
      
        .tab-navigation {
          background: #EBECF0;
          border-bottom: none;
          margin-bottom: 0;
          overflow-x: auto;
          padding: 8px 16px;
        }
      
        .tab-list {
          display: flex;
          list-style: none;
          margin: 0;
          padding: 3px;
          min-width: max-content;
          gap: 2px;
          background: #DFE1E6;
          border-radius: 8px;
          width: fit-content;
        }
      
        .tab-item { display: flex; }
      
        .tab-button {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 14px;
          border: none;
          border-radius: 6px;
          background: transparent;
          cursor: pointer;
          font-size: 13px;
          font-weight: 600;
          color: #505F79;
          transition: all 0.15s ease;
          white-space: nowrap;
          line-height: 1;
        }
      
        .tab-button:hover { color: #172B4D; background: rgba(255,255,255,0.6); }
      
        .tab-button.active {
          background: white;
          color: #0052CC;
          box-shadow: 0 1px 4px rgba(9,30,66,0.18), 0 0 1px rgba(9,30,66,0.10);
        }
      
        .tab-content {
          padding: 16px 20px 24px 20px;
          height: calc(100vh - 150px);
          overflow-y: auto;
          overflow-x: hidden;
          background: #F4F5F7;
        }

        .tab-content.reduced-padding {
          padding-top: 0;
        }
      
        .tab-content::-webkit-scrollbar {
          width: 8px;
        }
      
        .tab-content::-webkit-scrollbar-track {
          background: #F4F5F7;
          border-radius: 4px;
        }
      
        .tab-content::-webkit-scrollbar-thumb {
          background: #C1C7D0;
          border-radius: 4px;
        }
      
        .tab-content::-webkit-scrollbar-thumb:hover {
          background: #B3BAC5;
        }

        .content-container {
          max-width: 1500px;
        }

        @media (max-width: 768px) {
          .tab-content {
            padding: 0 16px 16px 16px;
          }
          
          .tab-list {
            padding: 0 16px;
          }
        }
      `}</style>
      
      {!hideNav && <nav className="tab-navigation">
        <ul className="tab-list">
          {tabs.map(tab => {
            const IconComponent = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <li key={tab.id} className="tab-item">
                <button
                  className={`tab-button ${isActive ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                  style={isActive ? { color: tab.color, borderBottomColor: tab.color } : {}}
                >
                  <IconComponent size={16} />
                  {tab.label}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>}
      
      <div className={`tab-content ${reducedPadding ? 'reduced-padding' : ''}`}>
        {renderContent()}
      </div>
    </div>
  );
};

export default Dashboard;
